import { RootStore } from '@/app/store'
import { makeAutoObservable } from 'mobx'
import { triggerBlobDownload } from '@/utils/download'
import JSZip from 'jszip'

export type ImageResult = {
  buffer: ArrayBuffer
  imageUrl: string
  thumbnailUrl?: string
  toolId: string
  inputs: Record<string, any>
  timestamp: number
}

export class ImageToolStore {
  rootStore: RootStore
  selectedImageUrl: string | null = null
  history: ImageResult[] = []
  maxHistoryLength = 10
  isHistoryVisible: boolean = false
  thumbnailSize = 100

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  public resetState() {
    this.clearSelectedImageUrl()
    this.history.forEach((result) => {
      if (result.imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(result.imageUrl)
      }
    })
    this.history = []
    this.isHistoryVisible = false
  }

  private async createThumbnail(
    buffer: ArrayBuffer,
    maxSize: number = this.thumbnailSize,
  ): Promise<string> {
    return new Promise((resolve) => {
      const blob = new Blob([buffer], { type: 'image/png' })
      const blobUrl = URL.createObjectURL(blob)
      const img = new Image()

      img.onload = () => {
        const aspectRatio = img.width / img.height
        let width = maxSize
        let height = maxSize

        if (aspectRatio > 1) {
          height = width / aspectRatio
        } else {
          width = height * aspectRatio
        }

        // Create canvas and draw the thumbnail
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')

        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height)
          const thumbnailUrl = canvas.toDataURL('image/png')
          URL.revokeObjectURL(blobUrl) // Clean up original blob URL
          resolve(thumbnailUrl)
        } else {
          // Fallback if canvas context not available
          resolve(blobUrl)
        }
      }

      img.onerror = () => {
        // Fallback if image loading fails
        resolve(blobUrl)
      }

      img.src = blobUrl
    })
  }

  public async addResult(result: ImageResult) {
    const blob = new Blob([result.buffer], { type: 'image/png' })
    const blobUrl = URL.createObjectURL(blob)
    result.imageUrl = blobUrl
    const thumbnailUrl = await this.createThumbnail(result.buffer)
    result.thumbnailUrl = thumbnailUrl
    this.setSelectedImageUrl(blobUrl)
    this.addToHistory(result)
  }

  public setSelectedImageUrl(url: string) {
    this.selectedImageUrl = url
  }

  public setHistoryVisible(isVisible: boolean) {
    this.isHistoryVisible = isVisible
  }

  public toggleHistoryVisible() {
    this.isHistoryVisible = !this.isHistoryVisible
  }

  private addToHistory(result: ImageResult) {
    if (this.history.length >= this.maxHistoryLength) {
      const oldestItem = this.history[this.maxHistoryLength - 1]
      if (oldestItem.imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(oldestItem.imageUrl)
      }
    }
    this.history = [result, ...this.history.slice(0, this.maxHistoryLength - 1)]
  }

  public clearSelectedImageUrl() {
    this.selectedImageUrl = null
  }

  public clearHistory() {
    this.history.forEach((result) => {
      if (result.imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(result.imageUrl)
      }
    })
    this.history = []
  }

  public getLatestResults(count: number = 10) {
    return this.history.slice(0, count)
  }

  public async downloadCurrentImage() {
    if (!this.selectedImageUrl) return

    try {
      const response = await fetch(this.selectedImageUrl)
      const blob = await response.blob()

      let filename = `image-${new Date().toISOString().split('T')[0]}.png`

      if (!filename.includes('.')) {
        filename += '.png'
      }

      triggerBlobDownload(filename, blob)
    } catch (error) {
      console.error('Failed to download image:', error)
    }
  }

  public downloadAllImagesAsZip() {
    if (this.history.length === 0) return

    const zip = new JSZip()
    const promises: Promise<void>[] = []

    this.history.forEach((result, index) => {
      const blob = new Blob([result.buffer], { type: 'image/png' })
      const timestamp = new Date(result.timestamp).toISOString().split('T')[0]
      const filename = `${result.toolId}-${timestamp}-${index}.png`

      promises.push(
        new Promise<void>((resolve) => {
          zip.file(filename, blob)
          resolve()
        }),
      )
    })

    Promise.all(promises).then(() => {
      zip.generateAsync({ type: 'blob' }).then((content) => {
        const currentDate = new Date().toISOString().split('T')[0]
        triggerBlobDownload(`image-tools-export-${currentDate}.zip`, content)
      })
    })
  }
}
